import { TTableCellTree, TTableInfo, TTableTreeModel } from './table.type';

const STICKY_HEADER_CELL_WIDTH_IN_PX = 120;
const calculateStickyHeader = (cellList: TTableCellTree[], stickyType: TTableCellTree['stickyType']) => {
  let stickyWidthCounter = 0;
  const hasStickyCell = cellList.find((cell) => cell.stickyType !== undefined);
  return cellList.map((v) => {
    const widthInPx = v.widthInPx ? v.widthInPx : STICKY_HEADER_CELL_WIDTH_IN_PX;
    const lastCounter = stickyWidthCounter;
    stickyWidthCounter += widthInPx;
    if (v.stickyType === stickyType) {
      return {
        ...v,
        widthInPx: widthInPx,
        stickyPosition: lastCounter,
      };
    } else if (hasStickyCell) {
      return {
        ...v,
        widthInPx: widthInPx,
      };
    }
    return v;
  });
};
const mergeCellTree = <T>(
  headerCellTree: TTableTreeModel<T>['tableInfo']['header']['cells'],
  rowsCellTree: TTableTreeModel<T>['tableInfo']['rows'][0]['cells'],
  finalTree: TTableTreeModel<T>['tableInfo']['rows'][0]['cells'],
) => {
  if (!headerCellTree.length) return [];

  const noOfCellInNode = headerCellTree.length;
  for (let index = 0; index < noOfCellInNode; index++) {
    const rowTreeNode = rowsCellTree[index];
    const headerTreeNode = {
      ...headerCellTree[index],
    };
    const headerCellChildren = headerTreeNode.children;
    if (rowTreeNode) {
      finalTree.push({
        ...rowTreeNode,
        widthInPx: headerTreeNode.widthInPx,
        maxWidthInPx: headerTreeNode.maxWidthInPx,
        stickyType: headerTreeNode.stickyType,
        stickyPosition: headerTreeNode.stickyPosition,
        isHidden: headerTreeNode.isHidden || false,
        children: headerCellChildren ? mergeCellTree(headerCellChildren, rowTreeNode.children || [], []) : [],
      });
    } else {
      finalTree.push({
        type: 'ERROR_CELL',
        children: headerCellChildren ? mergeCellTree(headerCellChildren, [], []) : [],
      });
    }
  }

  return finalTree;
};

const markLeaf = <T>(tableModel: TTableTreeModel<T>) => {
  const markLeafNodes = <T>(cells: TTableTreeModel<T>['tableInfo']['header']['cells']) => {
    if (cells && !cells.length) return;
    cells.forEach((v) => {
      if (v.children && v.children.length) {
        v.isLeaf = false;
        markLeafNodes(v.children);
      } else {
        v.isLeaf = true;
      }
    });
  };
  markLeafNodes(tableModel.tableInfo.header.cells);
  if (tableModel.tableInfo.footer) markLeafNodes(tableModel.tableInfo.footer.cells);

  tableModel.tableInfo.rows.forEach((element) => {
    markLeafNodes(element.cells);
  });
  return tableModel;
};

const mergeHeaderCellWithRow = <T>(tableTreeModel: TTableTreeModel<T>): TTableTreeModel<T> => {
  const { header, rows, footer } = tableTreeModel.tableInfo;
  // ---

  const addSticky = (cellList: TTableCellTree[]) => {
    const headerCalculateStickyLeft = calculateStickyHeader(cellList, 'LEFT');
    const headerCalculateStickyRight = calculateStickyHeader(headerCalculateStickyLeft.reverse(), 'RIGHT');
    return headerCalculateStickyRight.reverse();
  };

  const headerStickyHeaderCells = addSticky(header.cells);

  const updatedHeaderWithSticky: TTableTreeModel<T>['tableInfo']['header'] = {
    type: 'HEADER_ROW',
    cells: headerStickyHeaderCells,
  };
  // ---

  const newTableMode: TTableInfo<T>['rows'] = rows.map((v) => {
    const mergeCellUpdate = mergeCellTree(updatedHeaderWithSticky.cells, v.cells, []);
    const udateSelectInCells = mergeCellUpdate.map((cell) => {
      return {
        ...cell,
        ...(cell.type === 'SELECT_CELL' &&
          v.rowSelectionInfo && {
            isChecked: true,
          }),
      };
    });
    return {
      ...v,
      cells: [...udateSelectInCells],
    };
  });
  return {
    tableInfo: {
      ...tableTreeModel.tableInfo,
      header: updatedHeaderWithSticky,
      rows: newTableMode,
      ...(footer && { footer: { ...footer, cells: mergeCellTree(updatedHeaderWithSticky.cells, footer.cells, []) } }),
    },
    options: tableTreeModel.options,
  };
};

const logTableModelMsg =
  (message: string) =>
  <T>(tableModel: TTableTreeModel<T>) => {
    console.log('[LOG]', tableModel, message);
    return tableModel;
  };
const logTableModel = logTableModelMsg('');

const addPaddingColumn = <T>(tableModel: TTableTreeModel<T>): TTableTreeModel<T> => {
  const paddingColumn = tableModel.options?.padding;
  if (!paddingColumn) return tableModel;
  const textCell: TTableCellTree = {
    type: 'TEXT_CELL',
    text: '',
    widthInPx: paddingColumn,
  };
  const { header, rows, footer } = tableModel.tableInfo;
  return {
    ...tableModel,
    tableInfo: {
      header: { ...header, cells: [textCell, ...header.cells, textCell] },
      rows: rows.map((v) => ({
        ...v,
        cells: [textCell, ...v.cells, textCell],
      })),
      ...(footer ? { footer: { ...footer, cells: [textCell, ...header.cells, textCell] } } : {}),
    },
  };
};

const addSortOrderToHeaderConfig = <T>(tableModel: TTableTreeModel<T>): TTableTreeModel<T> => {
  if (!tableModel.options?.sort) return tableModel;
  return {
    ...tableModel,
    tableInfo: {
      ...tableModel.tableInfo,
      header: {
        ...tableModel.tableInfo.header,
        cells: tableModel.tableInfo.header.cells.map((v) => ({
          ...v,
          icon: { type: 'SORT', align: 'END' },
          sortOrder: 'DEFAULT_ORDER',
        })),
      },
    },
  };
};

const addFooterConfig = <T>(tableModel: TTableTreeModel<T>): TTableTreeModel<T> => {
  if (!tableModel.options?.footerConfig) return tableModel;
  return {
    ...tableModel,
    tableInfo: {
      ...tableModel.tableInfo,
      ...(tableModel.options.footerConfig && {
        footer: { type: 'FOOTER_ROW', cells: tableModel.options.footerConfig || [] },
      }),
    },
  };
};

const removeActionCellsInViewOnly = <T>(tableModel: TTableTreeModel<T>): TTableTreeModel<T> => {
  if (!tableModel.options?.isViewOnly) {
    return tableModel;
  }
  return {
    ...tableModel,
    tableInfo: {
      header: {
        ...tableModel.tableInfo.header,
        cells: tableModel.tableInfo.header.cells.filter((cell) => cell.type !== 'ACTION_CELL'),
      },
      rows: tableModel.tableInfo.rows.map((row) => ({
        ...row,
        cells: row.cells.filter((cell) => cell.type !== 'ACTION_CELL'),
      })),
    },
  };
};

export const TASK_COMMON_TABLE = {
  markLeaf,
  addPaddingColumn,
  mergeHeaderCellWithRow,
  removeActionCellsInViewOnly,
  // Log model state
  logTableModel,
  addSortOrderToHeaderConfig,
  addFooterConfig,
  logTableModelMsg,
};
